/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-string-refs */
import classnames from "classnames";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import ProductRatingMobile from "component/product/V2/reviews/ProductRatingMobile";
import VerticalSeparator from "component/product/vertical-separator/VerticalSeparator";
import { RootState } from "interfaces/generics.type";
import SafeLink from "modules/components/SafeLink";
import { useTranslation } from "next-i18next";
import { useSelector } from "react-redux";
import { isApparelProduct } from "util/product/apparel";
import ColorBox from "component/design-system/color-box/ColorBox";
import { Package } from "lucide-react";
import FinalSaleTag from "component/product/V2/image-section/FinalSaleTag";
import HideIf from "../../../util/HideIf";
import { LINE_PAGES } from "../../../util/product/product-constants";
import BOPIS from "../../bopis/BOPIS";
import ProductPrice from "../product-price/ProductPrice";
import SocialProof from "../social-proof/SocialProof";
import HeaderInfoButton from "./HeaderInfoButton";
import styles from "./ProductHeaderDetail.module.scss";
import ReviewRating from "./ReviewRating";
import StockLabel from "./StockLabel";

function ProductHeaderDetailWeb(props: any) {
  const {
    h1,
    corePrice,
    price,
    wasPrice,
    manufacturer,
    partNumber,
    outOfStock,
    limitedQuantityDetails,
    maximumPrice,
    minimumPrice,
    pageType,
    manufacturerPageUrl,
    productPrice = false,
    product,
    productFeatures = [],
  } = props;
  const { t } = useTranslation("product");

  const { businessCustomer } = useSelector((state: RootState) => state.authenticationState);

  const isApparel = isApparelProduct(product);
  const averageRating = product?.averageRating || 0;
  const totalReviewsCount = product?.totalReviewsCount || 0;
  const productColor = product?.filterableAttributes?.merchandiseColor || product?.filterableAttributes?.airFreshenerColor;
  const recommendedQuantity = product?.fitmentNote?.jsonNote?.fitmentNotes?.[0]?.q;

  return (
    <div className="row m-lr-0">
      <div className="col-md-12 col-lg-12 p-lr-0">
        <h1
          style={{
            marginTop: 8,
            marginBottom: 0,
            lineHeight: 1.2,
            paddingLeft: 0,
          }}
          className={`font-weight-600 col-md-12 col-lg-12 product-description color-black ${styles.subHeading}`}
        >
          {h1}
        </h1>
      </div>

      <div className="p-lr-0 col-md-12 col-lg-12">
        <div className="paragraph m-t-5">
          <div className={`${styles.flexContainer}`}>
            <HideIf show={!isApparel}>
              <div className={styles.part_brand_container}>
                <HideIf show={partNumber}>
                  <span className="d-flex align-items-baseline">
                    <PAText component="span" className="m-r-5" color="grey">
                      {`${t("Part")}#`}
                    </PAText>
                    <text
                      className="color-black"
                      style={{ paddingTop: "calc(0.3 * var(--safari-root-font, 10px))" }}
                    >
                      <PAText component="span" className={styles.part_number}>
                        {partNumber}
                      </PAText>
                    </text>
                  </span>
                </HideIf>
                {manufacturer && partNumber && (
                  <VerticalSeparator className={`${styles.vertical_separator} hidden-sm hidden-xs`} />
                )}

                <span className="d-flex m-r-5 align-items-baseline">
                  <PAText component="span" className="color-grey m-r-5">
                    {`${t(
                      "Brand",
                    )}:`}

                  </PAText>

                  <text
                    className="color-black d-flex"
                    style={{ paddingTop: "calc(0.2 * var(--safari-root-font, 10px))" }}
                  >
                    <SafeLink
                      href={manufacturerPageUrl}
                      className={classnames({
                        [styles.manufacturer_text]: manufacturerPageUrl,
                        [styles.no_link_text]: !manufacturerPageUrl,
                      })}
                    >
                      <PAText component="span" color="pa-blue">{manufacturer}</PAText>
                    </SafeLink>

                    {manufacturer === "Various Manufacturers" && (
                      <div style={{ width: 20 }}>
                        <HeaderInfoButton />
                      </div>
                    )}

                  </text>
                </span>
              </div>
            </HideIf>

            <HideIf show={productFeatures?.length}>
              <div className={`${styles.product_features}`}>
                {productFeatures.map((feature, idx: number) => (
                  <>
                    <PAText
                      color="medium-grey"
                    >
                      {feature}
                    </PAText>
                    <HideIf show={idx !== feature.length - 1}>
                      <VerticalSeparator className={`${styles.vertical_separator}`} />
                    </HideIf>
                  </>
                ))}

                <PAText
                  color="medium-grey"
                >
                  {productColor}
                </PAText>
              </div>
            </HideIf>
            <div className="visible-sm visible-xs">
              <ProductRatingMobile product={product} showSeparator={productFeatures?.length} />
            </div>
            <HideIf show={product?.boughtInLastMonth && !businessCustomer}>
              {/* added check for line page */}
              <HideIf show={product?.boughtInLastMonth && !isApparel && totalReviewsCount}>
                <VerticalSeparator className={styles.vertical_separator} />
              </HideIf>
              <SocialProof product={product} />
            </HideIf>

            <HideIf show={!outOfStock && !businessCustomer}>
              <div className="d-flex">
                <HideIf show={(!isApparel || (isApparel && productFeatures?.length))}>
                  <VerticalSeparator className={styles.vertical_separator} />
                </HideIf>
                <div className={styles.instock_mark_container}>
                  <div className={styles.instock_mark} />
                  <PAText fontSize={FONT_SIZE[0]} color="success-green">
                    In Stock
                  </PAText>
                </div>
              </div>

            </HideIf>
          </div>
          <BOPIS />

          <div className={styles.label_tags}>
            <div className="hidden-xs">
              <StockLabel {...{ outOfStock, limitedQuantityDetails }} />
            </div>
            <div>
              <FinalSaleTag product={product} />
            </div>

            <HideIf show={recommendedQuantity}>
              <ColorBox color="light-blue">
                <>
                  <Package color="#616161" size={15} />
                  <div>
                    <PAText color="pa-blue" fontSize={FONT_SIZE[0]}>{`${t("Recommended Quantity")}: `}</PAText>
                    <PAText color="pa-blue" bold fontSize={FONT_SIZE[0]}>{recommendedQuantity}</PAText>
                  </div>
                </>
              </ColorBox>
            </HideIf>
          </div>
        </div>

        <div className={`m-t-10 m-b-10 ${styles.hide_on_mobile}`}>
          <ReviewRating totalReviewsCount={totalReviewsCount} averageRating={averageRating} />
        </div>
        <HideIf show={productPrice}>
          <ProductPrice
            {...{
              price,
              wasPrice,
              maximumPrice,
              minimumPrice,
              corePrice,
            }}
            size="large"
            priceRangeValueClassName={`${styles.priceRangeValueClassName}`}
            productPriceClassName={`m-r-3  ${LINE_PAGES.includes(pageType)
              ? styles.productPriceRangeClassName
              : styles.productPriceClassName
              }`}
            color="#000000"
          />
        </HideIf>
      </div>
    </div>
  );
}

export default ProductHeaderDetailWeb;
